import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { ChevronLeft, ChevronRight, RotateCcw, RefreshCw, Settings, X } from 'lucide-react';

// Auth context
import { useAuth } from '../contexts/AuthContext';

// Components
import { ProgressIndicator } from '../components/onboarding/ProgressIndicator';
import { WelcomeStep } from '../components/onboarding/steps/WelcomeStep';
import { CompanyStep } from '../components/onboarding/steps/CompanyStep';
import { TargetingStep } from '../components/onboarding/steps/TargetingStep';
import { GoalsStep } from '../components/onboarding/steps/GoalsStep';
import { ReviewStep } from '../components/onboarding/steps/ReviewStep';

// Types and constants
import { OnboardingFormData, FormStep } from '../types/onboarding';
import { FORM_STEPS } from '../constants/onboarding';
import { 
  CompanyInformationFormData, 
  TargetCriteriaFormData, 
  CampaignGoalsFormData 
} from '../schemas/onboarding';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateOnboardingStatus } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState<FormStep[]>(FORM_STEPS);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set([0])); // Welcome step is always "completed"
  const [isResetMenuOpen, setIsResetMenuOpen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [formData, setFormData] = useState<any>({
    companyInformation: {},
    targetCriteria: {},
    campaignGoals: {},
    isDraft: true,
  });

  // Auto-save functionality
  useEffect(() => {
    const autoSave = () => {
      if (currentStepIndex > 0) {
        localStorage.setItem('onboarding-draft', JSON.stringify({
          formData,
          currentStepIndex,
          completedSteps: Array.from(completedSteps),
          lastSaved: new Date().toISOString(),
        }));
      }
    };

    const timer = setTimeout(autoSave, 2000);
    return () => clearTimeout(timer);
  }, [formData, currentStepIndex, completedSteps]);

  // Load draft on mount
  useEffect(() => {
    const saved = localStorage.getItem('onboarding-draft');
    if (saved) {
      try {
        const { 
          formData: savedData, 
          currentStepIndex: savedStep,
          completedSteps: savedCompleted 
        } = JSON.parse(saved);
        setFormData(savedData);
        setCurrentStepIndex(savedStep);
        if (savedCompleted) {
          setCompletedSteps(new Set(savedCompleted));
        }
        toast.success('Draft loaded from previous session');
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    }
  }, []);

  // Update step completion status
  useEffect(() => {
    setSteps(prevSteps => 
      prevSteps.map((step, index) => ({
        ...step,
        isCompleted: completedSteps.has(index),
        isActive: index === currentStepIndex,
      }))
    );
  }, [currentStepIndex, completedSteps]);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      // Mark current step as completed when navigating forward
      markStepAsCompleted(currentStepIndex);
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to completed steps or next step after current completed ones
    const maxAllowedStep = Math.max(...Array.from(completedSteps)) + 1;
    if (stepIndex <= maxAllowedStep) {
      setCurrentStepIndex(stepIndex);
    }
  };

  const markStepAsCompleted = (stepIndex: number) => {
    setCompletedSteps(prev => new Set(prev).add(stepIndex));
  };

  const canNavigateForward = () => {
    if (currentStepIndex >= steps.length - 1) return false;
    
    // Welcome step can always go forward
    if (currentStepIndex === 0) return true;
    
    // For other steps, check if current step is completed
    return completedSteps.has(currentStepIndex);
  };

  const canNavigateBack = () => {
    return currentStepIndex > 0;
  };

  const handleCompanyData = (data: CompanyInformationFormData) => {
    setFormData((prev: any) => ({
      ...prev,
      companyInformation: data,
    }));
    markStepAsCompleted(1); // Company step completed
    handleNext();
  };

  const handleTargetingData = (data: TargetCriteriaFormData) => {
    setFormData((prev: any) => ({
      ...prev,
      targetCriteria: data,
    }));
    markStepAsCompleted(2); // Targeting step completed
    handleNext();
  };

  const handleGoalsData = (data: CampaignGoalsFormData) => {
    setFormData((prev: any) => ({
      ...prev,
      campaignGoals: data,
    }));
    markStepAsCompleted(3); // Goals step completed
    handleNext();
  };

  const handleEditStep = (stepId: string) => {
    const stepIndex = steps.findIndex(step => step.id === stepId);
    if (stepIndex !== -1) {
      setCurrentStepIndex(stepIndex);
    }
  };

  const handleSaveDraft = () => {
    const draftData = {
      ...formData,
      isDraft: true,
      lastSaved: new Date().toISOString(),
    };
    
    localStorage.setItem('onboarding-draft', JSON.stringify({
      formData: draftData,
      currentStepIndex,
      completedSteps: Array.from(completedSteps),
      lastSaved: new Date().toISOString(),
    }));

    // Simulate API call to save draft
    toast.promise(
      new Promise(resolve => setTimeout(resolve, 1000)),
      {
        loading: 'Saving draft...',
        success: 'Draft saved successfully!',
        error: 'Failed to save draft',
      }
    );
  };

  const handleLaunch = async () => {
    if (!formData.companyInformation || !formData.targetCriteria || !formData.campaignGoals) {
      toast.error('Please complete all steps before launching');
      return;
    }

    const onboardingData = {
      companyInformation: {
        name: formData.companyInformation.name || '',
        website: formData.companyInformation.website || '',
        industries: formData.companyInformation.industries || [],
        size: formData.companyInformation.size || '',
        location: formData.companyInformation.location || '',
        description: formData.companyInformation.description || '',
        painPoints: formData.companyInformation.painPoints || [],
        uniqueValueProposition: formData.companyInformation.uniqueValueProposition || ''
      },
      targetCriteria: {
        description: formData.targetCriteria.description || '',
        jobTitles: formData.targetCriteria.jobTitles || [],
        industries: formData.targetCriteria.industries || [],
        companySizes: formData.targetCriteria.companySizes || [],
        locations: formData.targetCriteria.locations || [],
        buyingSignals: formData.targetCriteria.buyingSignals || [],
        exclusions: formData.targetCriteria.exclusions || '',
        idealCustomerProfile: formData.targetCriteria.idealCustomerProfile || ''
      },
      campaignGoals: {
        primaryObjective: formData.campaignGoals?.primaryObjective || '',
        timeline: formData.campaignGoals?.timeline || '',
        volumeExpectations: formData.campaignGoals?.volumeExpectations || '',
        budget: formData.campaignGoals?.budget || '',
        outreachChannels: formData.campaignGoals?.outreachChannels || [],
        additionalNotes: formData.campaignGoals?.additionalNotes || '',
        expectedResults: {}
      }
    };

    try {
      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Only add Authorization header if user has a token
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Show loading toast
      const toastPromise = toast.promise(
        fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/campaigns/onboarding`, {
          method: 'POST',
          headers,
          body: JSON.stringify(onboardingData)
        }).then(async (response) => {
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create campaign');
          }
          return response.json();
        }),
        {
          loading: 'Launching your campaign...',
          success: 'Campaign launched successfully! 🚀',
          error: (err) => `Failed to launch campaign: ${err.message}`,
        }
      );

      const result = await toastPromise;
      
      // Mark onboarding as completed (only if user is authenticated)
      if (token && updateOnboardingStatus) {
        updateOnboardingStatus(true);
      }
      
      // Clear draft and redirect
      localStorage.removeItem('onboarding-draft');
      
      // If user is not authenticated, show a success message and redirect to signup/login
      if (!token) {
        toast.success('Campaign setup complete! Please sign up to save and manage your campaign.');
        setTimeout(() => {
          navigate('/signup', { 
            state: { 
              fromOnboarding: true,
              campaignData: result.data
            }
          });
        }, 2000);
      } else {
        // Authenticated user - redirect to campaigns
        navigate('/campaigns', { 
          state: { 
            newCampaign: result.data,
            fromOnboarding: true 
          }
        });
      }
    } catch (error) {
      console.error('Error launching campaign:', error);
      // Toast error is already handled by toast.promise
    }
  };

  const handleStepComplete = useCallback((stepIndex: number, isComplete: boolean) => {
    if (isComplete) {
      setCompletedSteps(prev => new Set(prev).add(stepIndex));
    } else {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(stepIndex);
        return newSet;
      });
    }
  }, []);

  const handleTargetingStepComplete = useCallback((isComplete: boolean) => {
    handleStepComplete(2, isComplete);
  }, [handleStepComplete]);

  const handleGoalsStepComplete = useCallback((isComplete: boolean) => {
    handleStepComplete(3, isComplete);
  }, [handleStepComplete]);

  const handleResetCurrentPage = () => {
    const currentStep = steps[currentStepIndex];
    
    // Reset form data for current step
    switch (currentStep.id) {
      case 'company':
        setFormData((prev: any) => ({
          ...prev,
          companyInformation: {},
        }));
        break;
      case 'targeting':
        setFormData((prev: any) => ({
          ...prev,
          targetCriteria: {},
        }));
        break;
      case 'goals':
        setFormData((prev: any) => ({
          ...prev,
          campaignGoals: {},
        }));
        break;
      default:
        break;
    }

    // Remove current step from completed steps if it's not welcome
    if (currentStepIndex > 0) {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentStepIndex);
        return newSet;
      });
    }

    setIsResetMenuOpen(false);
    toast.success('Current step reset successfully');
  };

  const handleResetEntireForm = () => {
    setShowResetConfirm(false);
    setIsResetMenuOpen(false);
    
    // Reset all form data
    setFormData({
      companyInformation: {},
      targetCriteria: {},
      campaignGoals: {},
      isDraft: true,
    });
    
    // Reset navigation state
    setCurrentStepIndex(0);
    setCompletedSteps(new Set([0])); // Only welcome step completed
    
    // Clear localStorage
    localStorage.removeItem('onboarding-draft');
    
    toast.success('Form reset! Starting fresh from the beginning');
  };

  const handleConfirmReset = () => {
    setShowResetConfirm(true);
    setIsResetMenuOpen(false);
  };

  const renderCurrentStep = () => {
    const currentStep = steps[currentStepIndex];
    
    switch (currentStep.id) {
      case 'welcome':
        return (
          <WelcomeStep
            onNext={handleNext}
          />
        );
      
      case 'company':
        return (
          <CompanyStep
            data={formData.companyInformation || {} as CompanyInformationFormData}
            onNext={handleCompanyData}
            onBack={handleBack}
          />
        );
      
      case 'targeting':
        return (
          <TargetingStep
            data={formData.targetCriteria || {} as TargetCriteriaFormData}
            companyData={formData.companyInformation || {} as CompanyInformationFormData}
            onNext={handleTargetingData}
            onBack={handleBack}
            onStepComplete={handleTargetingStepComplete}
          />
        );
      
      case 'goals':
        return (
          <GoalsStep
            data={formData.campaignGoals || {} as CampaignGoalsFormData}
            onNext={handleGoalsData}
            onBack={handleBack}
            onStepComplete={handleGoalsStepComplete}
          />
        );
      
      case 'review':
        return (
          <ReviewStep
            data={formData as OnboardingFormData}
            onEdit={handleEditStep}
            onSaveDraft={handleSaveDraft}
            onLaunch={handleLaunch}
            onBack={handleBack}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50/30 overflow-auto">
      {/* Progress Indicator - only show after welcome step */}
      {currentStepIndex > 0 && (
        <ProgressIndicator
          steps={steps}
          currentStepIndex={currentStepIndex}
          onStepClick={handleStepClick}
        />
      )}

      {/* Main Content */}
      <div className={`${currentStepIndex > 0 ? 'pt-0' : 'pt-8'} pb-8`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            {renderCurrentStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation arrows - only show after welcome step */}
      {currentStepIndex > 0 && (
        <>
          {/* Back arrow */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleBack}
            disabled={!canNavigateBack()}
            title={canNavigateBack() ? "Go to previous step" : "Cannot go back"}
            className={`
              fixed left-4 top-1/2 -translate-y-1/2 z-30
              w-12 h-12 rounded-full shadow-strong transition-all duration-200
              flex items-center justify-center
              ${canNavigateBack()
                ? 'bg-white hover:bg-neutral-50 text-neutral-700 hover:text-neutral-900 hover:shadow-medium cursor-pointer hover:scale-105'
                : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
              }
            `}
          >
            <ChevronLeft className="w-6 h-6" />
          </motion.button>

          {/* Forward arrow */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleNext}
            disabled={!canNavigateForward()}
            title={
              canNavigateForward() 
                ? "Go to next step" 
                : "Complete current step to continue"
            }
            className={`
              fixed right-4 top-1/2 -translate-y-1/2 z-30
              w-12 h-12 rounded-full shadow-strong transition-all duration-200
              flex items-center justify-center
              ${canNavigateForward()
                ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-glow-primary hover:shadow-strong cursor-pointer hover:scale-105'
                : 'bg-neutral-200 text-neutral-400 cursor-not-allowed opacity-50'
              }
            `}
          >
            <ChevronRight className="w-6 h-6" />
            {!canNavigateForward() && currentStepIndex < steps.length - 1 && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning-500 rounded-full animate-pulse" />
            )}
          </motion.button>
        </>
      )}

      {/* Reset Menu - only show after welcome step */}
      {currentStepIndex > 0 && (
        <>
          {/* Reset Menu Button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={() => setIsResetMenuOpen(!isResetMenuOpen)}
            className={`
              fixed top-4 right-4 z-30
              w-12 h-12 rounded-full shadow-strong transition-all duration-200
              flex items-center justify-center
              ${isResetMenuOpen 
                ? 'bg-warning-500 hover:bg-warning-600 text-white' 
                : 'bg-white hover:bg-neutral-50 text-neutral-700 hover:text-neutral-900'
              }
              hover:shadow-medium cursor-pointer hover:scale-105
            `}
            title="Reset options"
          >
            {isResetMenuOpen ? <X className="w-5 h-5" /> : <RotateCcw className="w-5 h-5" />}
          </motion.button>

          {/* Reset Menu Dropdown */}
          <AnimatePresence>
            {isResetMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsResetMenuOpen(false)}
                  className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                />
                
                {/* Menu */}
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="fixed top-20 right-4 z-50 bg-white rounded-xl shadow-strong border border-neutral-200 overflow-hidden min-w-[240px]"
                >
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider border-b border-neutral-100 mb-2">
                      Reset Options
                    </div>
                    
                    {/* Reset Current Page */}
                    <button
                      onClick={handleResetCurrentPage}
                      className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-neutral-50 rounded-lg transition-colors group"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <RefreshCw className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">Reset Current Step</div>
                        <div className="text-xs text-neutral-500">Clear data from this step only</div>
                      </div>
                    </button>

                    {/* Reset Entire Form */}
                    <button
                      onClick={handleConfirmReset}
                      className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-red-50 rounded-lg transition-colors group"
                    >
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                        <RotateCcw className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <div className="font-medium text-red-900">Reset Entire Form</div>
                        <div className="text-xs text-red-500">Start over from the beginning</div>
                      </div>
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Reset Confirmation Dialog */}
      <AnimatePresence>
        {showResetConfirm && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              {/* Dialog */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl shadow-strong max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <RotateCcw className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    Reset Entire Form?
                  </h3>
                  <p className="text-neutral-600">
                    This will permanently delete all your progress and form data. 
                    You'll need to start over from the beginning.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    className="flex-1 px-4 py-3 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-xl font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetEntireForm}
                    className="flex-1 px-4 py-3 text-white bg-red-500 hover:bg-red-600 rounded-xl font-medium transition-colors"
                  >
                    Reset Form
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Keyboard shortcuts help */}
      {currentStepIndex > 0 && (
        <div className="fixed bottom-4 left-4 text-xs text-neutral-500 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-lg border border-neutral-200 z-40">
          <div className="flex items-center gap-4">
            <span>
              <kbd className="px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">←</kbd> Back
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">→</kbd> Next
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">Cmd+S</kbd> Save
            </span>
          </div>
        </div>
      )}

      {/* Keyboard shortcuts handler */}
      <div
        onKeyDown={(e) => {
          if (currentStepIndex === 0) return; // No shortcuts on welcome page
          
          if (e.key === 'ArrowLeft' && canNavigateBack()) {
            handleBack();
          } else if (e.key === 'ArrowRight' && canNavigateForward()) {
            handleNext();
          } else if ((e.metaKey || e.ctrlKey) && e.key === 's') {
            e.preventDefault();
            handleSaveDraft();
          }
        }}
        tabIndex={-1}
        className="fixed inset-0 pointer-events-none"
      />
    </div>
  );
};

export default OnboardingPage; 